import React, { useState, useMemo, useEffect } from 'react';
import { StyleSheet, Pressable } from 'react-native';
import { DNABox, DNAText, DNACard, DNAIcon, DNADivider, DNAButton } from '@alucio/lux-ui';
import {
  DNAThumbnail,
  DNAChip,
  useDNADocumentActions,
  DNACustomFieldChipList,
} from 'src/components/DNA';
import AIDisclaimer from 'src/components/Compliance/AIDisclaimer';
import { useAllDocumentVersionMap, useDocumentVersionORM } from 'src/state/redux/selector/document';
import { useDispatch } from 'src/state/redux';
import { useAppSettings } from 'src/state/context/AppSettings';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { resolveAISearchDeckReferences } from 'src/utils/customDeckRules/aiSearch';
import { AddToCustomPresentationModal } from './AddToCustomPresentationModal';
import {
  AIScientificSummary,
  ReferenceMetadata,
  AIScientificSummaryParagraph,
  AIScientificSummaryCitation,
  useDocumentSearchV2Context,
} from 'src/hooks/useDocumentSearchV2';
import { useUserTenant } from 'src/state/redux/selector/user';
import { AILoadingAnimation } from '../../../LoadingAnimation/AILoadingAnimation';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import useContentPageData from 'src/hooks/useContentPageData/useContentPageData';
import { getPresentable } from 'src/state/context/ContentProvider/helper';
import { DocumentVersionORM } from 'src/types/types';
import { getSlideTitle, getThumbURL } from 'src/utils/thumbnailHelpers';
import { ANALYTIC_CONTEXT_ENUM } from 'src/state/machines/analytics/analytics.types';
import { getHiddenSlidePageStateByNumber } from 'src/utils/hiddenSlides';
import { DocumentAccessLevel } from '@alucio/aws-beacon-amplify/src/models';

import {
  useAISearch,
  AISearchResultsProps,
  getTenantAIDisclaimer,
  getSummaryParagraphs,
  getNoAnswerParagraphs,
  collectAISearchReferences,
  shouldShowSummaryHeader,
} from './useAISearchHooks';

import DNADocumentVersionActionBar from 'src/components/DNA/Document/DNADocumentVersionActionBar';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';
import AISearchFeedback, {
  SearchRating,
  buildSearchRatingEventPayload,
} from './AISearchFeedback';

const CITATION_TOGGLE_KEY = 'SHOW_AI_SEARCH_CITATION_QUOTES';

const styles = StyleSheet.create({
  contentWrapper: {
    columnGap: 24,
    rowGap: 24,
    flexWrap: 'wrap',
    width: '100%',
    paddingHorizontal: 24,
    paddingTop: 32,
  },
  leftSideContainer: {
    flex: 1,
    minWidth: 400,
    rowGap: 24,
  },
  rightSideContainer: {
    flex: 1,
    minWidth: 400,
    rowGap: 24,
  },
  findingParagraph: {
    marginVertical: 4,
    alignSelf: 'flex-start',
  },
  inlineLinkIcon: {
    position: 'absolute',
    marginTop: -14,
  },
  citationsContainer: {
    paddingLeft: 12,
    borderLeftWidth: 2,
    borderLeftColor: colors['color-ui2-brand-100'],
  },
  loadingLeftSideContainer: {
    flex: 1,
    width: '100%',
  },
  referenceThumbnailContainer: {
    width: 180,
    flexShrink: 0,
    marginRight: 12,
  },
  referenceThumbnailLabel: {
    marginTop: 8,
  },
  referenceMetadataContainer: {
    marginStart: 12,
    minWidth: 0,
  },
  disclaimer: {
    marginTop: 4,
    padding: 8,
    borderRadius: 8,
    backgroundColor: colors['color-ui2-neutral-100'],
  },
});

const normalizeParagraphText = (text: string) => text
  .replace(/\s*\n+\s*/g, ' ')
  .replace(/[ \t]+/g, ' ')
  .trim();

type ReferenceDocumentVersionMap = Record<string, {
  relations?: {
    documentORM?: {
      model?: {
        accessLevel?: DocumentAccessLevel | keyof typeof DocumentAccessLevel;
      };
    };
  };
} | undefined>;

export const filterTenantReferences = (
  references: ReferenceMetadata[],
  allDocVersions: ReferenceDocumentVersionMap,
): ReferenceMetadata[] => references.filter((reference) => (
  allDocVersions[reference.id]?.relations?.documentORM?.model?.accessLevel === DocumentAccessLevel.TENANT
));

export const filterTenantSummaryParagraphs = (
  paragraphs: AIScientificSummaryParagraph[],
  allDocVersions: ReferenceDocumentVersionMap,
): AIScientificSummaryParagraph[] => paragraphs
  .map((paragraph) => ({
    ...paragraph,
    citations: (paragraph.citations ?? []).filter((citation) => (
      allDocVersions[citation.document_version_id]
        ?.relations?.documentORM?.model?.accessLevel === DocumentAccessLevel.TENANT
    )),
  }))
  .filter((paragraph) => paragraph.citations.length > 0);

const useReferenceSlideTitle = (
  docVerORM?: DocumentVersionORM,
  pageNumber?: number,
) => {
  const presentable = useMemo(
    () => (pageNumber ? getPresentable(docVerORM) : undefined),
    [docVerORM, pageNumber],
  );
  const { contentPageData } = useContentPageData(presentable, true);

  return useMemo(() => {
    if (!docVerORM || !pageNumber) {
      return '';
    }

    return getSlideTitle(docVerORM, pageNumber, contentPageData);
  }, [contentPageData, docVerORM, pageNumber]);
};

interface ParagraphItemProps {
  paragraph: AIScientificSummaryParagraph;
  isSelected?: boolean;
  onSelect?: () => void;
}

const CitationItem: React.FC<{ citation: AIScientificSummaryCitation }> = ({
  citation,
}) => {
  const sourceName = citation.source_name || citation.document_version_id;

  return (
    <DNABox appearance="col" spacing="xs">
      <DNABox appearance="row" alignY="center">
        <DNAText b2 status="subtle">
          {/* eslint-disable-next-line max-len */}
          {`Source name: ${sourceName} (Page ${citation.page_number}) Document Version ${citation.document_version_id}`}
        </DNAText>
      </DNABox>
      <DNAText b2 status="subtle" italic>
        {`Quoted text: "${citation.exact_quoted_text}"`}
      </DNAText>
    </DNABox>
  );
};

const ParagraphCitations: React.FC<{ citations: AIScientificSummaryCitation[] }> = ({
  citations,
}) => (
  <DNABox appearance="col" spacing="sm" style={styles.citationsContainer}>
    {citations.map((citation) => (
      <CitationItem
        key={`${citation.document_version_id}-${citation.page_number}-${citation.exact_quoted_text.substring(0, 20)}`}
        citation={citation}
      />
    ))}
  </DNABox>
);

const ParagraphItem: React.FC<ParagraphItemProps> = (props) => {
  const { paragraph, isSelected, onSelect } = props;
  const { allDocVersions } = useDocumentSearchV2Context();
  const [isHovered, setIsHovered] = useState(false);

  const showCitationsToggle = useMemo(() => {
    try {
      return localStorage.getItem(CITATION_TOGGLE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }, []);

  const hasCitations = useMemo(() => {
    if (!paragraph.citations || paragraph.citations.length === 0) return false;
    return paragraph.citations.some(
      c => c.document_version_id && c.document_version_id !== '<UNKNOWN>' && !!allDocVersions[c.document_version_id],
    );
  }, [paragraph.citations, allDocVersions]);

  return (
    <DNABox appearance="col" spacing="xs">
      <Pressable
        onPress={hasCitations ? onSelect : undefined}
        onHoverIn={() => hasCitations && setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        style={styles.findingParagraph}
        disabled={!hasCitations}
      >
        <DNAText b1>
          <DNAText b1 highlight={hasCitations && (isHovered || isSelected)}>
            {normalizeParagraphText(paragraph.narrative_text)}
          </DNAText>
          {hasCitations ? (
            <>
              {' '}
              <DNAIcon.Styled
                name="link"
                size="sm"
                color={colors['color-ui2-brand-600']}
                style={styles.inlineLinkIcon}
              />
            </>
          ) : ''}
        </DNAText>

      </Pressable>

      {showCitationsToggle && isSelected && paragraph.citations && paragraph.citations.length > 0 && (
        <DNABox appearance="col" style={{ marginTop: 8 }}>
          <ParagraphCitations citations={paragraph.citations} />
        </DNABox>
      )}
    </DNABox>
  );
};

interface ReferenceItemProps {
  reference: ReferenceMetadata;
}

interface ReferenceMetadataContentProps {
  reference: ReferenceMetadata;
  docVerORM: DocumentVersionORM;
}

const ReferenceMetadataContent: React.FC<ReferenceMetadataContentProps> = ({
  reference,
  docVerORM,
}) => {
  const referenceText = reference.quotedText || reference.text;
  const latestPublishedVersion =
    docVerORM.relations.documentORM.relations.version.latestPublishedDocumentVersionORM;
  const isNonModifiable = latestPublishedVersion?.meta.permissions.MSLNonModifiable;

  return (
    <DNABox
      appearance="col"
      fill
      shrink
      spacing="sm"
      style={styles.referenceMetadataContainer}
      alignY="center"
      testID="supporting-document-metadata"
    >
      <DNAPopover placement="top" fill disablePopover={['tabletPWA']}>
        <DNAPopover.Anchor style={{ flex: 1 }}>
          <DNAText b1 numberOfLines={3}>{reference.title}</DNAText>
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText b1 colorMode="dark">{reference.title}</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
      {referenceText && (
        <DNABox>
          <DNAText b2 status="tertiary" numberOfLines={3}>
            {normalizeParagraphText(referenceText)}
          </DNAText>
        </DNABox>
      )}
      <DNABox
        testID="supporting-document-badges"
        appearance="row"
        alignY="center"
        spacing="sm"
        wrap="start"
      >
        <DNACustomFieldChipList documentVersionORM={docVerORM} />
        {isNonModifiable && (
          <DNAChip.Beacon appearance="outline" status="secondary">
            Not for custom presentations
          </DNAChip.Beacon>
        )}
      </DNABox>
      <DNABox testID="supporting-document-actions">
        <DNADocumentVersionActionBar
          documentVersionORM={docVerORM}
          context={ANALYTIC_CONTEXT_ENUM.SEARCHQA}
        />
      </DNABox>
    </DNABox>
  );
};

const ReferenceItem: React.FC<ReferenceItemProps> = (props) => {
  const { reference } = props;
  const docVerORM = useDocumentVersionORM(reference.id);
  const parsedPageNumber = reference.page_number ? parseInt(reference.page_number, 10) : undefined;
  const pageNumber = parsedPageNumber && !Number.isNaN(parsedPageNumber)
    ? parsedPageNumber
    : undefined;
  const documentActions = useDNADocumentActions();
  const slideTitle = useReferenceSlideTitle(docVerORM, pageNumber);
  const hiddenSlideState = useMemo(() => getHiddenSlidePageStateByNumber({
    pages: docVerORM?.meta.allPages,
    pageNumber,
  }), [docVerORM?.meta.allPages, pageNumber]);

  if (!docVerORM) {
    return null;
  }

  const handlePress = () => {
    analytics?.track('SEARCH_RESULT_SELECTED', {
      category: 'SEARCH',
      documentVersionId: reference.id,
      result_type: 'searchQA',
    });
    documentActions.preview(docVerORM.relations.documentORM, pageNumber)();
  };

  const baseThumbnailKey = docVerORM.meta.assets.thumbnailKey;
  const thumbnailKey = baseThumbnailKey
    ? (pageNumber ? getThumbURL(docVerORM, pageNumber) : baseThumbnailKey)
    : undefined;

  return (

    <DNACard
      interactive="pressable"
      onPress={handlePress}
      appearance="outline"
      style={{ marginTop: 12 }}
    >
      <DNABox appearance="col" style={{ padding: 12 }}>
        <DNABox appearance="row">
          <DNABox appearance="col" style={styles.referenceThumbnailContainer}>
            <DNAThumbnail
              s3URL={thumbnailKey}
              height={102}
              width={180}
              isHidden={hiddenSlideState?.isHidden}
              sourceIsHidden={hiddenSlideState?.sourceIsHidden}
            />
            {!!pageNumber && (
              <DNAText
                b2
                bold
                numberOfLines={1}
                status="tertiary"
                style={styles.referenceThumbnailLabel}
              >
                {`${pageNumber}. ${slideTitle || `Slide ${pageNumber}`}`}
              </DNAText>
            )}
          </DNABox>
          <ReferenceMetadataContent
            reference={reference}
            docVerORM={docVerORM}
          />
        </DNABox>
      </DNABox>
    </DNACard>
  );
};

interface SummarySectionProps {
  loading: boolean;
  error: string | null;
  report: AIScientificSummary | null;
  statusMessage: string;
  hasSubmittedSearch: boolean;
  selectedFindingId?: number | null;
  onFindingSelect: (id: number | null) => void;
  searchText: string;
  summaryParagraphs: AIScientificSummaryParagraph[];
}

const SummaryHeader: React.FC<{ report: AIScientificSummary | null }> = ({ report }) => {
  const header = report?.scientific_summary?.header;
  const userTenant = useUserTenant();
  const aiDisclaimer = getTenantAIDisclaimer(userTenant?.config);
  const showSummaryLabel = !!header?.title;

  return (
    <DNABox appearance="col" spacing="xs">
      <DNABox appearance="col">
        <DNAText h3 bold>{'Scientific Summary'}</DNAText>
        {header && (
          <DNABox appearance="col" testID="ai-search-disclaimer" style={styles.disclaimer}>
            <AIDisclaimer b2 status="tertiary">{aiDisclaimer}</AIDisclaimer>
          </DNABox>
        )}
      </DNABox>
      {showSummaryLabel && (
        <DNABox appearance="row" alignY="center" spacing="xs">
          <DNAIcon.Styled
            name="sparkle-fill"
            size="sm"
            color={colors['color-ui2-brand-600']}
          />
          <DNAText b1 bold>{'AI Summary'}</DNAText>
        </DNABox>
      )}
    </DNABox>
  );
};

// ReportSection removed as it is no longer used in the new structure

interface ReportContentProps {
  summaryParagraphs: AIScientificSummaryParagraph[];
  selectedFindingId?: number | null;
  onFindingSelect: (id: number | null) => void;
}

const ReportContent: React.FC<ReportContentProps> = ({
  summaryParagraphs,
  selectedFindingId,
  onFindingSelect,
}) => (
  <DNABox appearance="col" spacing="sm">
    {summaryParagraphs.map((paragraph, idx) => (
      <ParagraphItem
        key={`ai-search-paragraph-${idx}`}
        paragraph={paragraph}
        isSelected={selectedFindingId === idx}
        onSelect={() => onFindingSelect(selectedFindingId === idx ? null : idx)}
      />
    ))}
  </DNABox>
);

export const SummarySection: React.FC<SummarySectionProps> = (props) => {
  const {
    loading,
    error,
    report,
    statusMessage,
    hasSubmittedSearch,
    selectedFindingId,
    onFindingSelect,
    searchText,
    summaryParagraphs,
  } = props;
  const hasParagraphs = summaryParagraphs.length > 0;
  const noAnswerParagraphs = getNoAnswerParagraphs(report);
  return (
    <DNABox appearance="col" spacing="sm" fill>
      {shouldShowSummaryHeader(report) && (
        <SummaryHeader report={report} />
      )}

      {error ? (
        <DNAText b1>
          {error}
        </DNAText>
      ) : noAnswerParagraphs.length > 0 ? (
        <DNABox appearance="col" spacing="sm">
          <DNAText h5>We couldn’t generate an answer</DNAText>
          {noAnswerParagraphs.map((paragraph, idx) => (
            <DNAText b1 key={`ai-search-no-answer-${idx}`}>
              {paragraph.narrative_text}
            </DNAText>
          ))}
        </DNABox>
      ) : hasParagraphs ? (
        <ReportContent
          summaryParagraphs={summaryParagraphs}
          selectedFindingId={selectedFindingId}
          onFindingSelect={onFindingSelect}
        />
      ) : loading ? (
        <DNABox
          appearance="col"
          alignY="center"
          alignX="center"
          fill
          spacing="sm"
          style={{ justifyContent: 'center' }}
        >
          <AILoadingAnimation message={statusMessage || 'Thinking...'} />
          <DNAText b1 status="tertiary" style={{ opacity: 0.8, textAlign: 'center' }}>
            You can continue using the app while the results are being generated.
          </DNAText>
        </DNABox>
      ) : (
        <DNABox appearance="col" fill spacing="sm">
          <DNAText h5>
            {searchText
              ? hasSubmittedSearch
                ? 'Your query did not generate any results. Please try a new one.'
                : 'Press Enter to run AI Search.'
              : 'Enter a search query to see the AI summary.'}
          </DNAText>
        </DNABox>
      )}
    </DNABox>
  );
};

interface ReferencesSectionProps {
  loading: boolean;
  report: AIScientificSummary | null;
  references: ReferenceMetadata[];
  selectedFinding?: AIScientificSummaryParagraph | null;
  onClearFilter: () => void;
}

interface ReferenceHeaderProps {
  isTabletPlatform: boolean;
  showClearFilter: boolean;
  onClearFilter: () => void;
  onAddToPresentation?: () => void;
  canAddToPresentation: boolean;
}

const ReferenceHeader: React.FC<ReferenceHeaderProps> = ({
  isTabletPlatform, showClearFilter, onClearFilter, onAddToPresentation, canAddToPresentation,
}) => {
  const addToPresentationButton = (
    <DNAButton
      appearance="filled"
      size="md"
      disabled={!canAddToPresentation}
      onPress={onAddToPresentation}
    >
      Add to custom presentation
    </DNAButton>
  );

  return (
    <DNABox
      appearance={isTabletPlatform ? 'col' : 'row'}
      alignY={isTabletPlatform ? undefined : 'center'}
      spacing={isTabletPlatform ? 'sm' : 'between'}
    >
      <DNABox appearance="row" alignY="center">
        <DNAIcon.Styled
          name="link"
          size="lg"
          style={{ marginRight: 12 }}
          color={colors['color-ui2-brand-600']}
        />
        <DNAText h4>Supporting Documents</DNAText>
      </DNABox>
      {(!!onAddToPresentation || showClearFilter) && (
        <DNABox appearance="row" alignX={isTabletPlatform ? 'start' : 'end'} alignY="center" spacing="sm">
          {!!onAddToPresentation && (
            canAddToPresentation ? addToPresentationButton : (
              <DNAPopover placement="top" disablePopover={['tabletPWA']}>
                <DNAPopover.Anchor>
                  <DNABox pointerEvents="none">
                    {addToPresentationButton}
                  </DNABox>
                </DNAPopover.Anchor>
                <DNAPopover.Content>
                  <DNAText b2 colorMode="dark">
                    These supporting documents cannot be used in a custom presentation
                  </DNAText>
                </DNAPopover.Content>
              </DNAPopover>
            )
          )}
          {showClearFilter && (
            <DNAButton
              appearance="ghost"
              status="tertiary"
              size="sm"
              iconLeft="x"
              onPress={onClearFilter}
            />
          )}
        </DNABox>
      )}
    </DNABox>
  );
};

export const ReferencesSection: React.FC<ReferencesSectionProps> = (props) => {
  const { loading, report, references, selectedFinding, onClearFilter } = props;
  const dispatch = useDispatch();
  const versions = useAllDocumentVersionMap();
  const customDeckEnabled = useFeatureFlags('BEAC_6655_custom_deck_usage');
  const aiSearchCustomDecksEnabled = useFeatureFlags('BEAC_7936_ai_search_custom_decks');
  const { isTabletPlatform } = useAppSettings();
  const filteredReferences = useMemo(() => {
    if (!selectedFinding) return references;
    const citedKeys = new Set(
      selectedFinding.citations
        ?.filter(c => c.document_version_id && c.document_version_id !== '<UNKNOWN>')
        .map(c => `${c.document_version_id}_${c.page_number || ''}`),
    );
    return references.filter(ref => citedKeys.has(`${ref.id}_${ref.page_number || ''}`));
  }, [references, selectedFinding]);

  const canAddToPresentation = !loading &&
    resolveAISearchDeckReferences(filteredReferences, versions).requestedCount > 0;
  const openPresentationModal = () => {
    if (!customDeckEnabled || !aiSearchCustomDecksEnabled || !canAddToPresentation) return;
    const snapshot = filteredReferences.map(reference => ({ ...reference }));
    dispatch(DNAModalActions.setModal({
      isVisible: true,
      allowBackdropCancel: true,
      component: modalProps => <AddToCustomPresentationModal {...modalProps} references={snapshot} />,
    }));
  };

  const messagingRefs = useMemo(
    () => filteredReferences.filter(ref => ref.isMessaging),
    [filteredReferences],
  );
  const normalRefs = useMemo(
    () => filteredReferences.filter(ref => !ref.isMessaging),
    [filteredReferences],
  );
  const hasMessagingDocs = messagingRefs.length > 0;

  const showAIDebug = useMemo(() => {
    try { return localStorage.getItem(CITATION_TOGGLE_KEY) === 'true'; } catch { return false; }
  }, []);

  if (loading) return null;

  return (
    <DNABox appearance="col" fill>
      <ReferenceHeader
        isTabletPlatform={isTabletPlatform}
        showClearFilter={!!(selectedFinding && filteredReferences.length < references.length)}
        onClearFilter={onClearFilter}
        onAddToPresentation={customDeckEnabled && aiSearchCustomDecksEnabled ? openPresentationModal : undefined}
        canAddToPresentation={canAddToPresentation}
      />

      {filteredReferences.length > 0 ? (
        <>
          {hasMessagingDocs && (
            <DNABox appearance="col" spacing="xs" style={{ marginTop: 12 }}>
              {showAIDebug && (
                <DNABox appearance="row" alignY="center" spacing="sm">
                  <DNAIcon.Styled
                    name="star"
                    size="sm"
                    color={colors['color-ui2-brand-600']}
                  />
                  <DNAText b2 bold status="brand">Messaging Documents</DNAText>
                </DNABox>
              )}
              {messagingRefs.map((ref) => (
                <ReferenceItem key={`messaging_${ref.id}_${ref.page_number || ''}`} reference={ref} />
              ))}
              {showAIDebug && normalRefs.length > 0 && (
                <DNADivider style={{ marginVertical: 12 }} />
              )}
            </DNABox>
          )}
          {normalRefs.map((ref) => (
            <ReferenceItem key={`${ref.id}_${ref.page_number || ''}`} reference={ref} />
          ))}
        </>
      ) : report && (
        <DNAText p1 style={{ marginTop: 20 }}>
          No specific references found for this selection.
        </DNAText>
      )}
    </DNABox>
  );
};

export const AISearchResults: React.FC<AISearchResultsProps> = (props) => {
  const { searchText } = props;
  const { allDocVersions } = useDocumentSearchV2Context();
  const {
    loading,
    error,
    report,
    statusMessage,
    references,
    flowId,
    activeSearchText,
  } = useAISearch({ searchText });

  const isQueuedSearch = activeSearchText === searchText && !loading && !report && !error;
  const isLoadingState = loading || isQueuedSearch;
  const [selectedFindingId, setSelectedFindingId] = useState<number | null>(null);
  const [selectedRating, setSelectedRating] = useState<SearchRating | null>(null);

  useEffect(() => {
    setSelectedFindingId(null);
    setSelectedRating(null);
  }, [searchText]);

  const resolvedReferences = useMemo(
    () => (report ? collectAISearchReferences(report, allDocVersions) : references),
    [allDocVersions, references, report],
  );
  const tenantReferences = useMemo(
    () => filterTenantReferences(resolvedReferences, allDocVersions),
    [allDocVersions, resolvedReferences],
  );
  const hasSupportingDocuments = tenantReferences.length > 0;
  const summaryParagraphs = useMemo(
    () => filterTenantSummaryParagraphs(report ? getSummaryParagraphs(report) : [], allDocVersions),
    [allDocVersions, report],
  );
  const hasSummaryResults = summaryParagraphs.length > 0;
  const displayReport = hasSummaryResults || report?.scientific_summary?.answer_found === false
    ? report : null;

  const selectedFinding = useMemo(() => {
    if (selectedFindingId === null) return null;
    return summaryParagraphs[selectedFindingId] || null;
  }, [selectedFindingId, summaryParagraphs]);

  const questionText = report?.scientific_summary?.header?.original_question || searchText;

  const handleRateSummary = (rating: SearchRating) => {
    if (selectedRating === rating) {
      return;
    }

    setSelectedRating(rating);
    analytics?.track('SEARCH_RATING', buildSearchRatingEventPayload(questionText, flowId, rating));
  };

  return (
    <DNABox appearance="row" fill style={styles.contentWrapper}>
      {/* LEFT SIDE: SUMMARY */}
      <DNABox
        appearance="col"
        spacing="lg"
        style={isLoadingState && !report ? styles.loadingLeftSideContainer : styles.leftSideContainer}
      >
        <SummarySection
          loading={isLoadingState}
          error={error}
          report={displayReport}
          statusMessage={statusMessage}
          hasSubmittedSearch={activeSearchText === searchText}
          selectedFindingId={selectedFindingId}
          onFindingSelect={setSelectedFindingId}
          searchText={searchText}
          summaryParagraphs={summaryParagraphs}
        />
        {hasSummaryResults && (
          <AISearchFeedback
            selectedRating={selectedRating}
            onRate={handleRateSummary}
          />
        )}
      </DNABox>
      {/* RIGHT SIDE: REFERENCES */}
      {(!isLoadingState || report) && hasSupportingDocuments && (
        <DNABox appearance="col" spacing="lg" style={styles.rightSideContainer}>
          <ReferencesSection
            loading={isLoadingState}
            report={displayReport}
            references={tenantReferences}
            selectedFinding={selectedFinding}
            onClearFilter={() => setSelectedFindingId(null)}
          />
        </DNABox>
      )}
    </DNABox>
  );
};

export { styles as AISearchResultsStyles };

export default AISearchResults;
